import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileSrc, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import MontagesGallery from "@/components/MontagesGallery";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Image as ImageIcon, Loader2, Search, Download, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  ["all", "Toutes"],
  ["before", "Avant"],
  ["after", "Après"],
  ["pair", "Avant/Après"],
];

export default function Gallery() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/gallery");
        setData(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
    })();
  }, []);

  const matchQ = (name) => !q || (name || "").toLowerCase().includes(q.toLowerCase());

  const photos = useMemo(() => {
    if (!data) return [];
    let list = data.photos || [];
    if (filter === "before" || filter === "after") list = list.filter((p) => p.type === filter);
    return list.filter((p) => matchQ(p.client_name));
  }, [data, filter, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pairs = useMemo(() => {
    if (!data) return [];
    return (data.groups || []).filter((g) => g.before?.length && g.after?.length).filter((g) => matchQ(g.client_name));
  }, [data, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPair = async (g) => {
    try {
      const load = (url) => new Promise((res, rej) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = fileSrc(url);
      });
      const [b, a] = await Promise.all([load(g.before[0].url), load(g.after[0].url)]);
      const H = 800;
      const wB = Math.round((b.width / b.height) * H);
      const wA = Math.round((a.width / a.height) * H);
      const gap = 8, pad = 44;
      const canvas = document.createElement("canvas");
      canvas.width = wB + wA + gap;
      canvas.height = H + pad;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(b, 0, pad, wB, H);
      ctx.drawImage(a, wB + gap, pad, wA, H);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 22px sans-serif";
      ctx.fillText("AVANT", 14, 30);
      ctx.fillText("APRÈS", wB + gap + 14, 30);
      ctx.font = "16px sans-serif";
      ctx.fillStyle = "#34d399";
      ctx.textAlign = "right";
      ctx.fillText(`${g.client_name || ""} · ${g.date || ""}`, canvas.width - 14, 30);
      const url = canvas.toDataURL("image/jpeg", 0.92);
      const link = document.createElement("a");
      link.href = url;
      link.download = `avant-apres-${(g.client_name || "prestation").replace(/\s+/g, "-")}-${g.date}.jpg`;
      link.click();
      toast.success("Comparatif avant/après téléchargé");
    } catch (e) {
      toast.error("Téléchargement impossible pour ces images");
    }
  };

  return (
    <div>
      <PageHeader title="Galerie Photos" subtitle="Toutes vos photos avant / après, reliées à vos prestations" icon={ImageIcon} />
      <MontagesGallery />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="gallery-counter">
          <span className="font-semibold text-slate-900">{data?.photos_count ?? 0}</span> photos
          <span className="text-slate-300">•</span>
          <span className="font-semibold text-slate-900">{data?.appointments_count ?? 0}</span> prestations
        </div>
        <div className="relative sm:ml-auto sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="gallery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom client" className="pl-9 bg-white border-slate-200" />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5 bg-white border border-slate-200 rounded-lg p-1 w-fit">
        {FILTERS.map(([k, lbl]) => (
          <button key={k} data-testid={`gallery-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-md text-sm transition ${filter === k ? "bg-emerald-500 text-black font-semibold" : "text-slate-500 hover:text-slate-900"}`}>{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : filter === "pair" ? (
        pairs.length === 0 ? <Empty /> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="gallery-pairs">
            {pairs.map((g) => (
              <div key={g.appointment_id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`pair-${g.appointment_id}`}>
                <div className="grid grid-cols-2 gap-0.5 bg-slate-100">
                  <PairImg url={g.before[0].url} tag="AVANT" onClick={() => setLightbox({ url: g.before[0].url, ...g, type: "before" })} />
                  <PairImg url={g.after[0].url} tag="APRÈS" onClick={() => setLightbox({ url: g.after[0].url, ...g, type: "after" })} />
                </div>
                <div className="flex items-center justify-between gap-2 p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{g.client_name}</div>
                    <div className="text-xs text-slate-500 truncate">{g.vehicle_name || g.title || "—"} · {g.date}</div>
                  </div>
                  <Button size="sm" onClick={() => downloadPair(g)} data-testid={`pair-download-${g.appointment_id}`} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold shrink-0"><Download className="w-4 h-4 mr-1.5" /> Avant/Après</Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : photos.length === 0 ? <Empty /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="gallery-grid">
          {photos.map((p, i) => (
            <button key={`${p.appointment_id}-${p.type}-${i}`} data-testid={`gallery-photo-${i}`} onClick={() => setLightbox(p)}
              className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
              <img src={fileSrc(p.url)} alt={p.client_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
              <span className={`absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${p.type === "before" ? "bg-slate-900/80 text-white" : "bg-emerald-500 text-black"}`}>{p.type === "before" ? "AVANT" : "APRÈS"}</span>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition">
                <div className="text-[11px] text-white font-medium truncate">{p.client_name}</div>
                <div className="text-[10px] text-white/70 truncate">{p.date}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl p-0 overflow-hidden">
          {lightbox && (
            <div>
              <div className="bg-slate-900 flex items-center justify-center max-h-[70vh]">
                <img src={fileSrc(lightbox.url)} alt="" className="max-h-[70vh] w-auto object-contain" data-testid="lightbox-image" />
              </div>
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{lightbox.client_name} <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${lightbox.type === "before" ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>{lightbox.type === "before" ? "AVANT" : "APRÈS"}</span></div>
                  <div className="text-xs text-slate-500 truncate">{lightbox.vehicle_name || lightbox.title || "—"} · {lightbox.date}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {lightbox.client_id && <Button size="sm" variant="outline" onClick={() => navigate(`/clients/${lightbox.client_id}`)} data-testid="lightbox-view-client" className="border-slate-200"><ExternalLink className="w-4 h-4 mr-1.5" /> Client</Button>}
                  <Button size="sm" variant="ghost" onClick={() => setLightbox(null)}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PairImg({ url, tag, onClick }) {
  return (
    <button onClick={onClick} className="relative aspect-square bg-slate-100">
      <img src={fileSrc(url)} alt="" loading="lazy" className="w-full h-full object-cover" />
      <span className={`absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${tag === "AVANT" ? "bg-slate-900/80 text-white" : "bg-emerald-500 text-black"}`}>{tag}</span>
    </button>
  );
}

function Empty() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="gallery-empty">
      <ImageIcon className="w-10 h-10 mx-auto text-slate-300 mb-3" />
      <p className="text-sm text-slate-500">Aucune photo pour ce filtre. Ajoutez des photos Avant/Après depuis vos rendez-vous.</p>
    </div>
  );
}
